import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Package2, Plus, X } from "lucide-react";
import { insertProductSchema, type InsertProduct, type Category } from "@/lib/schema";

interface CategoryOption {
  value: string;
  label: string;
  level: number;
}

export default function AdminNewProductForm() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const form = useForm<InsertProduct>({
    resolver: zodResolver(insertProductSchema),
    defaultValues: {
      name: "",
      description: "",
      price: "Consultar precio",
      categoryId: undefined,
      features: [],
      technicalSpecs: [],
      imageUrl: "",
      inStock: true,
    },
  });

  const { data: categories } = useQuery<Category[]>({
    queryKey: ["/api/categories/all"],
  });

  const createProductMutation = useMutation({
    mutationFn: async (data: InsertProduct) => {
      const response = await apiRequest("POST", "/api/products", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      toast({
        title: "Producto creado",
        description: "El producto ha sido creado correctamente.",
      });
      setLocation("/admin");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "No se pudo crear el producto. " + error.message,
        variant: "destructive",
      });
    },
  });

  // Procesar las categorías para mostrarlas de forma jerárquica
  const getCategoryOptions = (categories?: Category[]): CategoryOption[] => {
    if (!categories) return [];

    const options: CategoryOption[] = [];
    const mainCategories = categories.filter(c => !c.parentId);

    const addCategoryWithChildren = (category: Category, level: number) => {
      const prefix = level === 0 ? "📁 " : level === 1 ? "└─ 📂 " : "   └─ 📄 ";
      options.push({
        value: category.id.toString(),
        label: prefix + category.name,
        level
      });

      const children = categories.filter(c => c.parentId === category.id);
      children.forEach(child => addCategoryWithChildren(child, level + 1));
    };

    mainCategories.forEach(category => addCategoryWithChildren(category, 0));
    return options;
  };

  const categoryOptions = getCategoryOptions(categories);

  const onSubmit = (data: InsertProduct) => {
    createProductMutation.mutate(data);
  };

  return (
    <div className="container mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package2 className="w-6 h-6 text-primary" />
            Nuevo Producto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información básica */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre del producto</FormLabel>
                        <FormControl>
                          <Input placeholder="Ej: Envasadora al vacío profesional" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descripción</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe las características principales del producto..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Precio</FormLabel>
                        <FormControl>
                          <Input placeholder="Ej: Consultar precio, 1500€, etc." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="categoryId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoría</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona una categoría" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categoryOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                className={`pl-${option.level * 4}`}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Características y especificaciones */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="features"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Características</FormLabel>
                        <div className="space-y-2">
                          {field.value.map((feature, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Input
                                value={feature}
                                onChange={(e) => {
                                  const newFeatures = [...field.value];
                                  newFeatures[index] = e.target.value;
                                  field.onChange(newFeatures);
                                }}
                                placeholder="Ej: Alta capacidad de producción"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const newFeatures = field.value.filter((_, i) => i !== index);
                                  field.onChange(newFeatures);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => field.onChange([...field.value, ""])}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Añadir característica
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="technicalSpecs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Especificaciones técnicas</FormLabel>
                        <div className="space-y-2">
                          {field.value.map((spec, index) => (
                            <div key={index} className="grid grid-cols-2 gap-2">
                              <Input
                                value={spec.label}
                                onChange={(e) => {
                                  const newSpecs = [...field.value];
                                  newSpecs[index] = { ...spec, label: e.target.value };
                                  field.onChange(newSpecs);
                                }}
                                placeholder="Etiqueta (ej: Potencia)"
                              />
                              <div className="flex items-center gap-2">
                                <Input
                                  value={spec.value}
                                  onChange={(e) => {
                                    const newSpecs = [...field.value];
                                    newSpecs[index] = { ...spec, value: e.target.value };
                                    field.onChange(newSpecs);
                                  }}
                                  placeholder="Valor (ej: 2200W)"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    const newSpecs = field.value.filter((_, i) => i !== index);
                                    field.onChange(newSpecs);
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => field.onChange([...field.value, { label: "", value: "" }])}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Añadir especificación
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL de la imagen</FormLabel>
                        <FormControl>
                          <Input placeholder="https://..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="inStock"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel>Disponible en stock</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/admin")}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={createProductMutation.isPending}>
                  {createProductMutation.isPending ? "Creando..." : "Crear producto"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
